import { create } from 'zustand';

/**
 * Store d'authentification — volontairement petit.
 *
 * Où vivent les jetons :
 *  - accessToken  → en mémoire seulement. Perdu au rechargement, retrouvé
 *                   silencieusement par le jeton de renouvellement.
 *  - refreshToken → localStorage. C'est lui qui fait qu'on ne se reconnecte
 *                   pas après un rechargement de page.
 *  - user         → en mémoire.
 *
 * Le raisonnement : un vol du jeton de renouvellement ne donne rien tant qu'il
 * n'est pas échangé, et cet échange laisse une trace (adresse, agent) dans le
 * journal des connexions. Garder le jeton d'accès hors du stockage réduit la
 * prise d'une injection de script à l'onglet ouvert.
 *
 * La MÉCANIQUE de session — renouvellement unique, inactivité, déconnexion —
 * vit dans `@/lib/session`. Ici on ne garde que l'état.
 */

const REFRESH_KEY = 'md.refresh_token';

export type SessionUser = {
  accountId: number;
  email: string;
  displayName: string;
  source: string;
  roles: string[];
  expiresAt: string;
};

/** Pourquoi la session s'est terminée — l'écran de connexion le dit. */
export type SessionEndReason = 'user' | 'idle' | 'expired' | null;

type SessionExtras = {
  idleTimeoutSec?: number;
  sessionExpiresAt?: string;
};

type AuthState = {
  accessToken: string | null;
  refreshToken: string | null;
  user: SessionUser | null;

  /** Délai d'inactivité annoncé par le serveur, en secondes. */
  idleTimeoutSec: number | null;
  /** Fin de la session, quoi qu'il arrive (plafond absolu). */
  sessionExpiresAt: string | null;
  /** Secondes restantes avant coupure, quand l'avertissement est affiché. */
  idleWarning: number | null;
  /** Renseigné à la fermeture, lu par l'écran de connexion. */
  endReason: SessionEndReason;

  setSession: (
    accessToken: string,
    refreshToken: string,
    user: SessionUser,
    extras?: SessionExtras,
  ) => void;
  setAccessToken: (accessToken: string) => void;
  setUser: (user: SessionUser) => void;
  setIdleWarning: (seconds: number | null) => void;
  setEndReason: (reason: SessionEndReason) => void;
  /** Vide l'état local. La fermeture côté serveur est faite par `endSession`. */
  clear: () => void;
};

function readRefresh(): string | null {
  try {
    return localStorage.getItem(REFRESH_KEY);
  } catch {
    return null;
  }
}

function writeRefresh(token: string | null) {
  try {
    if (token) localStorage.setItem(REFRESH_KEY, token);
    else localStorage.removeItem(REFRESH_KEY);
  } catch {
    /* navigation privée avec stockage refusé */
  }
}

/**
 * Lit le jeton de renouvellement dans le stockage plutôt que dans le store.
 *
 * La distinction compte au moment de renouveler : un autre onglet a pu faire
 * tourner le jeton entre-temps, et le serveur ferme la session si on lui
 * présente un jeton déjà consommé. Le stockage est la source de vérité, la
 * copie en mémoire n'est qu'un reflet.
 */
export function readRefreshToken(): string | null {
  return readRefresh();
}

export const useAuth = create<AuthState>((set) => ({
  accessToken: null,
  refreshToken: readRefresh(),
  user: null,
  idleTimeoutSec: null,
  sessionExpiresAt: null,
  idleWarning: null,
  endReason: null,
  setSession: (accessToken, refreshToken, user, extras) => {
    writeRefresh(refreshToken);
    set({
      accessToken,
      refreshToken,
      user,
      idleWarning: null,
      endReason: null,
      ...(extras?.idleTimeoutSec ? { idleTimeoutSec: extras.idleTimeoutSec } : {}),
      ...(extras?.sessionExpiresAt ? { sessionExpiresAt: extras.sessionExpiresAt } : {}),
    });
  },
  setAccessToken: (accessToken) => set({ accessToken }),
  setUser: (user) => set({ user }),
  setIdleWarning: (seconds) =>
    set((s) => (s.idleWarning === seconds ? s : { ...s, idleWarning: seconds })),
  setEndReason: (endReason) => set({ endReason }),
  clear: () => {
    writeRefresh(null);
    set({
      accessToken: null,
      refreshToken: null,
      user: null,
      idleWarning: null,
      sessionExpiresAt: null,
    });
  },
}));

/** Lit le jeton d'accès courant sans s'abonner au store. */
export function currentAccessToken(): string | null {
  return useAuth.getState().accessToken;
}
